import { computed, reactive, watch, toRefs } from 'vue'
import { useStore } from '@freebe/sdk/store'
import isNumber from 'lodash/isNumber'
import dayjs from 'dayjs'
import QuarterPlugin from 'dayjs/plugin/quarterOfYear'
import AdvancedFormatPlugin from 'dayjs/plugin/advancedFormat'
import CustomParsePlugin from 'dayjs/plugin/customParseFormat'
import 'dayjs/locale/fr'

dayjs.locale('fr')
dayjs.extend(QuarterPlugin)
dayjs.extend(AdvancedFormatPlugin)
dayjs.extend(CustomParsePlugin)

const FETCHED = []

export default function useStatistics(params = {}) {
  const { get, find, fetch, create } = useStore('@admin/statistics/periodic')

  const options = computed(() => ({
    year: params?.year || null,
    month: isNumber(params.month) ? params.month : null,
    from: params?.from || null,
    to: params?.to || null,
  }))

  const state = reactive({
    year: computed(() => options.value.year),
    month: computed(() => options.value.month),
    from: computed(() => options.value.from),
    to: computed(() => options.value.to),
    statistic: computed(() => {
      if (state.hasRange) {
        return find({
          year: {
            $gte: state.from.getFullYear(),
            $lte: state.to.getFullYear()
          },
          $sort: { year: 1 }
        })
      } else if (state.year) {
        return get({
          year: state.year,
        })
      }
    }),

    hasRange: computed(() =>
      state.from !== null && state.to !== null
    ),

    rangeYears: computed(() => {
      const start = state.from ? dayjs(state.from).year() : 0 
      const end = state.to ? dayjs(state.to).year() : 0

      return new Array(end - start + 1).fill(0).map((_, index) => start + index)
    }),

    rangeMonths: computed(() => {
      if (state.hasRange) {
        const start = {
          year: dayjs(state.from).year(),
          month: dayjs(state.from).month()
        }
        const end = {
          year: dayjs(state.to).year(),
          month: dayjs(state.to).month()
        }

        return state.statistic.reduce((aggro, stat) => {
          const isStartYear = stat.year === start.year
          const isEndYear = stat.year === end.year

          if (isStartYear) {
            aggro.push(...stat.months.slice(start.month, isEndYear ? end.month + 1 : 12))
          } else if (stat.year === end.year) {
            aggro.push(...stat.months.slice(0, end.month + 1))
          } else {
            aggro.push(...stat.months)
          }

          return aggro
        }, [])
      } else {
        return state.statistic?.months || []
      }
    }),

    uniqueUsers: computed(getStatTotal('unique_users')),
    uniqueUsersByMonth: computed(getStatByMonth('unique_users')),
    totalUniqueUsers: computed(() => {
      const currentMonth = state.statistic?.months[state.month]?.['unique_users'] || 0
      let previousMonth = state.month > 0 
        ? state.statistic?.months[state.month - 1]?.['unique_users'] || 0
        : null

      let total = 0
      
      if (previousMonth === null) {
        if (state.lastYear?.statistic?.value) {
          previousMonth = state.lastYear.statistic.value.months[11]['unique_users']

          total = currentMonth - previousMonth
        }
      } else {
        total = currentMonth - previousMonth
      }

      return total
    }),
    
    newUsers: computed(getStatTotal('new_users')),
    newUsersByMonth: computed(getStatByMonth('new_users')),
    newUsersEvolution: computed(() => {
      let evolution = null

      if (state.lastMonth !== null) {
        evolution = (state.newUsersByMonth - state.lastMonth.new_users) / state.lastMonth.new_users * 100

        if (evolution === Infinity || Number.isNaN(evolution)) {
          evolution = 0
        }
      }

      return {
        month: dayjs().month(state.month - 1 >= 0 ? state.month - 1 : 11).format('MMMM'),
        percent: evolution !== null ? `${parseInt(evolution, 10)} %` : 'N/A',
        applicable: evolution !== null,
        increase: evolution !== null ? evolution >= 0 : false,
        decrease: evolution !== null ? evolution < 0 : false,
      }
    }),

    removedUsers: computed(getStatTotal('removed_users')),
    removedUsersByMonth: computed(getStatByMonth('removed_users')),
    removedUsersEvolution: computed(() => {
      let evolution = null

      if (state.lastMonth !== null) {
        evolution = (state.removedUsersByMonth - state.lastMonth.removed_users) / state.lastMonth.removed_users * 100

        if (evolution === Infinity || Number.isNaN(evolution)) {
          evolution = 0
        }
      }

      return {
        month: dayjs().month(state.month - 1 >= 0 ? state.month - 1 : 11).format('MMMM'),
        percent: evolution !== null ? `${parseInt(evolution, 10)} %` : 'N/A',
        applicable: evolution !== null,
        increase: evolution !== null ? evolution >= 0 : false,
        decrease: evolution !== null ? evolution < 0 : false,
      }
    }),

    churnUsers: computed(getStatTotal('churn_rate')),
    churnUsersByMonth: computed(getStatByMonth('churn_rate')),
    churnUsersEvolution: computed(() => {
      let evolution = null

      if (state.lastMonth !== null) {
        evolution = (state.churnUsersByMonth - state.lastMonth.churn_rate) / state.lastMonth.churn_rate * 100

        if (evolution === Infinity || Number.isNaN(evolution)) {
          evolution = 0
        }
      }

      return {
        month: dayjs().month(state.month - 1 >= 0 ? state.month - 1 : 11).format('MMMM'),
        percent: evolution !== null ? `${parseInt(evolution, 10)} %` : 'N/A',
        applicable: evolution !== null,
        increase: evolution !== null ? evolution >= 0 : false,
        decrease: evolution !== null ? evolution < 0 : false,
      }
    }),

    ca: computed(getStatTotal('ca')),
    caByMonth: computed(getStatByMonth('ca')),

    vat: computed(getStatTotal('vat')),
    vatByMonth: computed(getStatByMonth('vat')),

    subscriptionsPlans: computed(sumNestedObjects('plans')),
    freeCodes: computed(sumNestedObjects('free_codes')),
    paidCodes: computed(sumNestedObjects('paid_codes')),

    newSuscribedUsers: computed(getStatTotal('new_subscribed_users')),
    newSuscribedUsersByMonth: computed(getStatByMonth('new_subscribed_users')),
    newSuscribedUsersEvolution: computed(() => {
      let evolution = null

      if (state.lastMonth !== null) {
        evolution = (state.newSuscribedUsersByMonth - state.lastMonth.new_subscribed_users) / state.lastMonth.new_subscribed_users * 100

        if (evolution === Infinity || Number.isNaN(evolution)) {
          evolution = 0
        }
      }
      
      return {
        month: dayjs().month(state.month - 1 >= 0 ? state.month - 1 : 11).format('MMMM'),
        percent: evolution !== null ? `${parseInt(evolution, 10)} %` : 'N/A',
        applicable: evolution !== null,
        increase: evolution !== null ? evolution >= 0 : false,
        decrease: evolution !== null ? evolution < 0 : false,
      }
    }),

    newFreeUsers: computed(getStatTotal('new_free_users')),
    newFreeUsersByMonth: computed(getStatByMonth('new_free_users')),
    newFreeUsersEvolution: computed(() => {
      let evolution = null

      if (state.lastMonth !== null) {
        evolution = (state.newFreeUsersByMonth - state.lastMonth.new_free_users) / state.lastMonth.new_free_users * 100

        if (evolution === Infinity || Number.isNaN(evolution)) {
          evolution = 0
        }
      }

      return {
        month: dayjs().month(state.month - 1 >= 0 ? state.month - 1 : 11).format('MMMM'),
        percent: evolution !== null ? `${parseInt(evolution, 10)} %` : 'N/A',
        applicable: evolution !== null,
        increase: evolution !== null ? evolution >= 0 : false,
        decrease: evolution !== null ? evolution < 0 : false,
      }
    }),

    endSuscribedUsers: computed(getStatTotal('end_subscribed_users')),
    endSuscribedUsersByMonth: computed(getStatByMonth('end_subscribed_users')),
    endSuscribedUsersEvolution: computed(() => {
      let evolution = null

      if (state.lastMonth !== null) {
        evolution = (state.endSuscribedUsersByMonth - state.lastMonth.end_subscribed_users) / state.lastMonth.end_subscribed_users * 100

        if (evolution === Infinity || Number.isNaN(evolution)) {
          evolution = 0
        }
      }

      return {
        month: dayjs().month(state.month - 1 >= 0 ? state.month - 1 : 11).format('MMMM'),
        percent: evolution !== null ? `${parseInt(evolution, 10)} %` : 'N/A',
        applicable: evolution !== null,
        increase: evolution !== null ? evolution >= 0 : false,
        decrease: evolution !== null ? evolution < 0 : false,
      }
    }),

    endFreeUsers: computed(getStatTotal('end_free_users')),
    endFreeUsersByMonth: computed(getStatByMonth('end_free_users')),
    endFreeUsersEvolution: computed(() => {
      let evolution = null

      if (state.lastMonth !== null) {
        evolution = (state.endFreeUsersByMonth - state.lastMonth.end_free_users) / state.lastMonth.end_free_users * 100

        if (evolution === Infinity || Number.isNaN(evolution)) {
          evolution = 0
        }
      }

      return {
        month: dayjs().month(state.month - 1 >= 0 ? state.month - 1 : 11).format('MMMM'),
        percent: evolution !== null ? `${parseInt(evolution, 10)} %` : 'N/A',
        applicable: evolution !== null,
        increase: evolution !== null ? evolution >= 0 : false,
        decrease: evolution !== null ? evolution < 0 : false,
      }
    }),

    invoicesCount: computed(getStatTotal('invoices_count')),
    invoicesCountByMonth: computed(getStatByMonth('invoices_count')),
    invoicesCountEvolution: computed(() => {
      let evolution = null

      if (state.lastMonth !== null) {
        evolution = (state.invoicesCountByMonth - state.lastMonth.invoices_count) / state.lastMonth.invoices_count * 100

        if (evolution === Infinity || Number.isNaN(evolution)) {
          evolution = 0
        }
      }

      return {
        month: dayjs().month(state.month - 1 >= 0 ? state.month - 1 : 11).format('MMMM'),
        percent: evolution !== null ? `${parseInt(evolution, 10)} %` : 'N/A',
        applicable: evolution !== null,
        increase: evolution !== null ? evolution >= 0 : false,
        decrease: evolution !== null ? evolution < 0 : false,
      }
    }),

    invoicesAmount: computed(getStatTotal('invoices_amount')),
    invoicesAmountByMonth: computed(getStatByMonth('invoices_amount')),
    invoicesAmountEvolution: computed(() => {
      let evolution = null

      if (state.lastMonth !== null) {
        evolution = (state.invoicesAmountByMonth - state.lastMonth.invoices_amount) / state.lastMonth.invoices_amount * 100

        if (evolution === Infinity || Number.isNaN(evolution)) {
          evolution = 0
        }
      }

      return {
        month: dayjs().month(state.month - 1 >= 0 ? state.month - 1 : 11).format('MMMM'),
        percent: evolution !== null ? `${parseInt(evolution, 10)} %` : 'N/A',
        applicable: evolution !== null,
        increase: evolution !== null ? evolution >= 0 : false,
        decrease: evolution !== null ? evolution < 0 : false,
      }
    }),

    quotationsCount: computed(getStatTotal('quotations_count')),
    quotationsCountByMonth: computed(getStatByMonth('quotations_count')),
    quotationsCountEvolution: computed(() => {
      let evolution = null

      if (state.lastMonth !== null) {
        evolution = (state.quotationsCountByMonth - state.lastMonth.quotations_count) / state.lastMonth.quotations_count * 100

        if (evolution === Infinity || Number.isNaN(evolution)) {
          evolution = 0
        }
      }

      return {
        month: dayjs().month(state.month - 1 >= 0 ? state.month - 1 : 11).format('MMMM'),
        percent: evolution !== null ? `${parseInt(evolution, 10)} %` : 'N/A',
        applicable: evolution !== null,
        increase: evolution !== null ? evolution >= 0 : false,
        decrease: evolution !== null ? evolution < 0 : false,
      }
    }),

    quotationsAmount: computed(getStatTotal('quotations_amount')),
    quotationsAmountByMonth: computed(getStatByMonth('quotations_amount')),
    quotationsAmountEvolution: computed(() => {
      let evolution = null

      if (state.lastMonth !== null) {
        evolution = (state.quotationsAmountByMonth - state.lastMonth.quotations_amount) / state.lastMonth.quotations_amount * 100

        if (evolution === Infinity || Number.isNaN(evolution)) {
          evolution = 0
        }
      }

      return {
        month: dayjs().month(state.month - 1 >= 0 ? state.month - 1 : 11).format('MMMM'),
        percent: evolution !== null ? `${parseInt(evolution, 10)} %` : 'N/A',
        applicable: evolution !== null,
        increase: evolution !== null ? evolution >= 0 : false,
        decrease: evolution !== null ? evolution < 0 : false,
      }
    }),

    period: computed(() => {
      let date = dayjs().year(state.year)
      let format = '[Année]'
      let months = state.rangeMonths.map(stat => ({ month: stat.month, year: stat.year }))
      let years =  state.rangeMonths.map(stat => stat.year)

      if (isNumber(state.month)) {
        format = 'MMMM'
        date = date.month(state.month)
        months = months.splice(state.month, 1)
      } else if (state.hasRange) {
        format = `${dayjs(state.from).format('MMM YYYY')} à ${dayjs(state.to).format('MMM YYYY')}`
      }

      return {
        formatted: state.hasRange ? format : date.format(format),
        months: months.map(({ month, year }) =>
          dayjs().month(month).year(year).format('MMMM YYYY').toUpperCase()
        ),
        years: years.map(year =>
          dayjs().year(year).format('YYYY')
        )
      }
    }),

    lastMonth: computed(() => {
      if (state.statistic === null || state.month === null) {
        return null
      }
      
      let statistic = state.statistic.months[state.month - 1]

      if (state.month === 0) {
        if (state.lastYear?.statistic?.value) {
          statistic = state.lastYear.statistic.value.months[11]
        }
      }

      return statistic || null
    }),

    lastYear: computed(() => {
      if (state.year === null) {
        return null
      } else {
        return useStatistics({ year: state.year - 1 })
      }
    }),

    evolution: computed(() => {
      let evolution = null

      if (state.lastMonth !== null) {
        evolution = (state.caByMonth - state.lastMonth.ca) / state.lastMonth.ca * 100

        if (evolution === Infinity || Number.isNaN(evolution)) {
          evolution = 0
        }
      }

      return {
        month: dayjs().month(state.month - 1 >= 0 ? state.month - 1 : 11).format('MMMM'),
        percent: evolution !== null ? `${parseInt(evolution, 10)} %` : 'N/A',
        applicable: evolution !== null,
        increase: evolution !== null ? evolution >= 0 : false,
        decrease: evolution !== null ? evolution < 0 : false,
      }
    }),
  })

  function getStatTotal (name) {
    return function () {
      let total = 0

      if (state.statistic !== null) {
        if (state.year !== null) {
          if (state.month === null) {
            for ( let i = 0; i < state.statistic.months.length; i ++) {
              total += state.statistic.months[i][name]
            }
          } else if (state.month !== null) {
            total = state.statistic?.months[state.month]?.[name] || 0
          } 
        } else if (state.hasRange) {
          for ( let i = 0; i < state.rangeMonths.length; i ++) {
            total += state.rangeMonths[i][name]
          }
        }
      }

      return total
    }
  }

  function getStatByMonth (name) {
    return function () {
      let months = []

      if (state.statistic !== null) {
        if (state.year !== null) {
          if (state.month === null) {
            months = state.statistic?.months?.map(month => month[name])
          } else if (state.month !== null) {
            months = state.statistic?.months[state.month]?.[name] || 0
          }
        } else if (state.hasRange) {
          months = state.rangeMonths?.map(stat => stat[name])
        }
      }

      return months
    }
  }

  function sumNestedObjects (name) {
    return function () {
      let aggro = []

      if (state.statistic !== null) {
        if (state.year !== null) {
          if (state.month === null) {
            state.statistic?.months?.reduce((currentAggro, month) => {
              month[name].forEach(item => {
                const index = currentAggro.findIndex(p => p.name === item.name)
  
                if (index >= 0) {
                  currentAggro[index].total += item.total
                } else {
                  currentAggro.push({ ...item })
                }
              })
  
              return currentAggro
            }, aggro)
          } else if (state.month !== null) {
            state.statistic?.months[state.month]?.[name]?.forEach(item => {
              const index = aggro.findIndex(p => p.name === item.name)

              if (index >= 0) {
                aggro[index].total += item.total
              } else {
                aggro.push({ ...item })
              }
            })
          }
        } else if (state.hasRange) {
          state.rangeMonths.reduce((currentAggro, month) => {
            month[name].forEach(item => {
              const index = currentAggro.findIndex(p => p.name === item.name)

              if (index >= 0) {
                currentAggro[index].total += item.total
              } else {
                currentAggro.push({ ...item })
              }
            })

            return currentAggro
          }, aggro)
        }
      }

      return aggro
    }
  }

  function fetchStatistics(year = state.year) {
    if (!state.statistic && !FETCHED.includes(year)) {
      FETCHED.push(year)

      fetch(year).catch(async error => {
        if (error.code === 404) {
          try {
            await create({ year })
          } catch (error) {
            console.error('Error creating statistic', error)
          }
        }
      })
    }
  }

  function fetchRange () {
    const start = dayjs(state.from).year()
    const end = dayjs(state.to).year()
    
    FETCHED.push(...state.rangeYears)
    
    fetch({
      query: {
        since: start,
        until: end
      }
    })
  }

  watch(() => options.value.year, () => fetchStatistics(), { immediate: false })
  watch(() => options.value.from, () => fetchRange(), { immediate: false })
  watch(() => options.value.to, () => fetchRange(), { immediate: false })

  if (state.hasRange) {
    fetchRange()
  } else {
    fetchStatistics()
  }


  return toRefs(state)
}
